import React, { useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";

interface FileDropzoneProps {
  onFileSelect: (file: File) => void;
  onClear?: () => void;
  selectedFile?: File;
  acceptedFileExtensions?: string[]; // e.g. ['.xlsx', '.xls']
  maxFileSize?: number; // in bytes
  disabled?: boolean;
  className?: string;
  instructionText?: string;
}

export const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFileSelect,
  onClear,
  selectedFile,
  acceptedFileExtensions = [".xlsx", ".xls"],
  disabled = false,
  className = "",
  instructionText = "Excel files only (.xlsx, .xls)",
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateAndSetFile = (newFile: File) => {
    const extensionPart = newFile.name.split(".").pop();
    const extension = `.${extensionPart?.toLowerCase() ?? ""}`;
    if (
      acceptedFileExtensions.length > 0 &&
      !acceptedFileExtensions.includes(extension)
    ) {
      toast.error(
        `Upload failed: Only ${acceptedFileExtensions.join(
          ", "
        )} files are allowed`
      );
      return;
    }
    onFileSelect(newFile);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleManualFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndSetFile(e.target.files[0]);
    }
    // Reset input so same file can be selected again
    if (e.target) e.target.value = "";
  };

  const handleZoneClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClear) {
      onClear();
    }
  };

  return (
    <div className={`w-full ${className}`}>
      <div
        onClick={handleZoneClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`flex flex-col items-center justify-center w-full min-h-[160px] border-2 border-dashed rounded-lg transition-colors duration-200 ${
          isDragActive && !disabled
            ? "border-blue-500 bg-blue-50"
            : disabled
            ? "border-gray-200 bg-gray-50 cursor-not-allowed"
            : "border-gray-300 bg-gray-50 hover:bg-gray-100 cursor-pointer"
        }`}
      >
        <div className="flex flex-col items-center justify-center w-full h-full p-6 pointer-events-none">
          <Icon
            icon="material-symbols:cloud-upload-outline"
            className={`mb-3 ${disabled ? "text-gray-300" : "text-gray-400"}`}
            height={48}
            width={48}
          />
          <p
            className={`text-sm mb-1 ${
              disabled ? "text-gray-400" : "text-gray-600"
            }`}
          >
            <span
              className={`font-semibold ${
                disabled ? "text-gray-400" : "text-blue-600 hover:underline"
              }`}
              onClick={handleZoneClick}
            >
              Click to upload
            </span>{" "}
            or drag and drop
          </p>
          <p
            className={`text-xs ${
              disabled ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {instructionText}
          </p>

          {selectedFile && (
            <div className="mt-4 flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm border border-blue-100 pointer-events-auto">
              <Icon icon="mdi:file-excel-outline" height={16} />
              <span className="font-medium truncate max-w-[200px]">
                {selectedFile.name}
              </span>
              {onClear && !disabled && (
                <button
                  onClick={handleClear}
                  className="ml-1 hover:text-red-500 transition-colors"
                  type="button"
                >
                  <Icon icon="mdi:close-circle" height={16} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept={acceptedFileExtensions.join(",")}
        onChange={handleManualFileChange}
        disabled={disabled}
      />
    </div>
  );
};
